import debounce from "lodash.debounce";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BiSearch } from "react-icons/bi";
import { BsEye, BsViewList } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { usePagination, useTable } from "react-table";
import Loader from "../../components/Loader";
import Api from "../../service/http";

const Inbox = () => {
    const navigate = useNavigate();
    const [textMessages, setTextMessages] = useState([]);
    const [pageCount, setPageCount] = useState(0);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [viewMessage, setViewMessage] = useState(null);

    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

    const fetchAlerts = async (searchTerm = '', page = 1) => {
        setLoading(true);
        try {
            const response = await Api.get(`web/text-messages?page=${page}&search=${searchTerm}`);
            const data = response.data;
            setTextMessages(data?.data);
            setPageCount(data?.last_page || 0);
        } catch (error) {
            console.error('Error fetching alerts:', error);
        } finally {
            setLoading(false);
        }
    };

    const debouncedFetch = useCallback(debounce((value, page) => {
        fetchAlerts(value, page);
    }, 500), []);

    useEffect(() => {
        debouncedFetch(search, pagination.pageIndex + 1);
    }, [search, pagination.pageIndex]);

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setPagination(prev => ({ ...prev, pageIndex: 0 }));
    };

    const columns = useMemo(() => [
        { Header: "Name", accessor: "name", Cell: ({ row }) => <span className="text-sm font-medium text-gray-900">{row?.original?.user?.first_name || 'Unknown'}</span> },
        { Header: "Number", accessor: "phone_number", Cell: ({ value }) => <span className="text-sm text-gray-600">{value}</span> },
        {
            Header: "Message", accessor: "message", Cell: ({ value }) => (
                <div className="max-w-xs sm:max-w-md lg:max-w-lg">
                    <p className="text-sm text-gray-600 truncate" title={value}>{value}</p>
                </div>
            )
        },
        { 
            Header: "Type", 
            accessor: "type", 
            Cell: ({ value }) => (
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                    value === 'inbound' 
                    ? 'bg-blue-50 text-blue-700 border-blue-100' 
                    : 'bg-indigo-50 text-indigo-700 border-indigo-100'
                }`}>
                    {value}
                </span>
            ) 
        },
        { Header: "Time", accessor: "created_at", Cell: ({ value }) => <span className="text-sm text-gray-500">{moment(value).fromNow()}</span> },
        {
            Header: "Actions",
            Cell: ({ row }) => (
                <div className="flex items-center gap-2">
                    <button
                        className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        onClick={() => setViewMessage(row.original.message)}
                        title="View Full Message"
                    >
                        <BsEye className="text-lg" />
                    </button>
                    {row?.original?.user && (
                        <button
                            className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            onClick={() => navigate(`/inbox/${row?.original?.user?.id}`)}
                            title="View Conversation"
                        >
                            <BsViewList className="text-lg" />
                        </button>
                    )}
                </div>
            ),
        },
    ], [navigate]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount: tablePageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data: textMessages,
            initialState: { pageIndex: pagination.pageIndex, pageSize: pagination.pageSize },
            manualPagination: true,
            pageCount,
        },
        usePagination
    );

    useEffect(() => {
        setPagination({ pageIndex, pageSize });
    }, [pageIndex, pageSize]);

    return (
        <div className="p-0 h-full overflow-y-auto scrollbar-hide bg-gray-50/50">
            <div className="max-w-7xl mx-auto p-4 sm:p-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Inbox</h1>
                        <p className="text-gray-500 text-sm mt-1">Review and manage incoming and outgoing text messages.</p>
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-4 border-b border-gray-100 bg-gray-50/30">
                        <div className="relative">
                            <BiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                            <input
                                type="text"
                                placeholder="Search messages by content or sender..."
                                className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm"
                                value={search}
                                onChange={handleSearchChange}
                            />
                        </div>
                    </div>

                    <div className="overflow-x-auto hidden md:block">
                        <table {...getTableProps()} className="w-full">
                            <thead>
                                {headerGroups.map(headerGroup => (
                                    <tr {...headerGroup.getHeaderGroupProps()} className="bg-gray-50/50">
                                        {headerGroup.headers.map(column => (
                                            <th
                                                {...column.getHeaderProps()}
                                                className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100"
                                            >
                                                {column.render('Header')}
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody {...getTableBodyProps()} className="divide-y divide-gray-100">
                                {loading ? (
                                    <tr>
                                        <td colSpan={columns.length} className="py-20">
                                            <Loader size="lg" className="mx-auto" />
                                        </td>
                                    </tr>
                                ) : page?.length > 0 ? (
                                    page.map(row => {
                                        prepareRow(row);
                                        return (
                                            <tr {...row.getRowProps()} className="hover:bg-gray-50/50 transition-colors">
                                                {row.cells.map(cell => (
                                                    <td
                                                        {...cell.getCellProps()}
                                                        className="px-6 py-4 text-sm text-gray-600"
                                                    >
                                                        {cell.render('Cell')}
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                                            <div className="flex flex-col items-center">
                                                <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mb-3">
                                                    <BiSearch className="text-2xl text-gray-300" />
                                                </div>
                                                <p>No messages found matching your criteria</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="block md:hidden">
                        {loading ? (
                            <div className="py-20">
                                <Loader size="lg" className="mx-auto" />
                            </div>
                        ) : page?.length > 0 ? (
                            <div className="divide-y divide-gray-100">
                                {page.map(row => {
                                    prepareRow(row);
                                    const { original } = row;
                                    return (
                                        <div key={original.id} className="p-4 space-y-3 hover:bg-gray-50/50 transition-colors">
                                            <div className="flex justify-between items-start">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-indigo-50 rounded-full flex items-center justify-center text-indigo-600 font-bold text-sm">
                                                        {(original?.user?.first_name || 'U').charAt(0)}
                                                    </div>
                                                    <div>
                                                        <h4 className="text-sm font-bold text-gray-900">{original?.user?.first_name || 'Unknown'}</h4>
                                                        <p className="text-xs text-gray-500">{original.phone_number}</p>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col gap-1 items-end">
                                                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                                                        original.type === 'inbound' 
                                                        ? 'bg-blue-50 text-blue-700 border-blue-100' 
                                                        : 'bg-indigo-50 text-indigo-700 border-indigo-100'
                                                    }`}>
                                                        {original.type}
                                                    </span>
                                                    <span className="text-[10px] text-gray-400">{moment(original.created_at).fromNow()}</span>
                                                </div>
                                            </div>
                                            
                                            <div className="bg-gray-50/50 rounded-xl p-3">
                                                <p className="text-sm text-gray-700 line-clamp-3 leading-relaxed">
                                                    {original.message}
                                                </p>
                                            </div>

                                            <div className="flex items-center justify-between pt-1">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        className="p-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all"
                                                        onClick={() => setViewMessage(original.message)}
                                                        title="View"
                                                    >
                                                        <BsEye className="text-lg" />
                                                    </button>
                                                    {original?.user && (
                                                        <button
                                                            className="p-2 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-xl transition-all"
                                                            onClick={() => navigate(`/inbox/${original?.user?.id}`)}
                                                            title="View Conversation"
                                                        >
                                                            <BsViewList className="text-lg" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="px-6 py-12 text-center text-gray-500">
                                <div className="flex flex-col items-center">
                                    <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mb-3">
                                        <BiSearch className="text-2xl text-gray-300" />
                                    </div>
                                    <p className="text-sm">No messages found</p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50/30">
                        <div className="text-sm text-gray-500 font-medium">
                            Showing page <span className="text-gray-900">{pageIndex + 1}</span> of <span className="text-gray-900">{pageOptions.length}</span>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => previousPage()}
                                disabled={!canPreviousPage}
                                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-all shadow-sm"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => nextPage()}
                                disabled={!canNextPage}
                                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-all shadow-sm"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {viewMessage && (
                <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden transform transition-all">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="text-xl font-bold text-gray-900">Message Content</h3>
                            <button 
                                onClick={() => setViewMessage(null)}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="bg-gray-50 rounded-xl p-4 text-gray-700 text-sm leading-relaxed max-h-[60vh] overflow-y-auto">
                                {viewMessage}
                            </div>
                            <div className="mt-6 flex justify-end">
                                <button
                                    className="px-6 py-2 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-all text-sm"
                                    onClick={() => setViewMessage(null)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Inbox;
